import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client'
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import { setContext } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { LocalState } from '@apollo/client/local-state'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'
import { getImpersonationToken, getOriginalToken, getUserToken, logout } from './utils/tokenManagement'
import { hasGraphQLErrorCode } from './utils/apolloError'
import { omitDeep } from './utils/omitDeep'
import { API_URL, ENVIRON } from './env'
import packageJson from '../package.json'

if (ENVIRON === 'development' || ENVIRON === 'vitest') {
  loadDevMessages()
  loadErrorMessages()
}

const uploadLink = new UploadHttpLink({
  uri: `${API_URL}/graphql`,
  fetch: (uri: RequestInfo | URL, options?: RequestInit) =>
    fetch(uri, {
      ...options,
      headers:
        options?.body instanceof FormData
          ? { ...(options.headers ?? {}), 'Apollo-Require-Preflight': 'true' }
          : (options?.headers ?? {}),
    }),
})

const authLink = setContext((_, { headers }) => {
  const token = getUserToken()
  const impersonationToken = getImpersonationToken()
  const originalToken = getOriginalToken()

  return {
    headers: {
      ...headers,
      'X-App-Version': packageJson.version,
      Authorization: token ? token : '',
      ...(impersonationToken && originalToken ? { 'authorization-original': originalToken } : {}),
    },
  }
})

const activityFeedEntityPossibleTypes = {
  ActivityFeedEntity: [
    'AdCopy',
    'AdGroup',
    'AdGroupDirectoryPreset',
    'AdTemplateBanner',
    'AdTemplateNative',
    'AdTemplateWeb',
    'Advertiser',
    'Agency',
    'BillingContact',
    'Campaign',
    'CampaignProgrammatic',
    'CompanyApiToken',
    'Position',
    'Publisher',
    'Reseller',
    'SegmentBehavioural',
    'SegmentCrm',
    'TradingDesk',
    'User',
    'Website',
  ],
}

const cache: InMemoryCache = new InMemoryCache({
  possibleTypes: activityFeedEntityPossibleTypes,
  typePolicies: {
    Query: {
      fields: {
        isLoggedIn() {
          return isLoggedInVar()
        },
        searchActivityFeedGrouped: {
          keyArgs: ['companyId', 'userId', 'entityType', 'origin'],
          merge(_existing, incoming) {
            return incoming
          },
        },
      },
    },
  },
})

export const isLoggedInVar = cache.makeVar<boolean>(!!localStorage.getItem('token'))

const errorLink = new ErrorLink(({ error }) => {
  if (hasGraphQLErrorCode(error, 'UNAUTHENTICATED')) logout(apolloClient, true)
  else if (error) console.log(`[Network error]: ${error.message}`)
})

const cleanMutationTypeName = new ApolloLink((operation, forward) => {
  if (operation.variables) operation.variables = omitDeep(operation.variables, ['__typename'])

  return forward(operation)
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, cleanMutationTypeName, uploadLink]),
  cache,
  localState: new LocalState(),
})
